import { useState, useEffect } from 'react';
import { quoteBet } from '../api/api';
import { BetItem } from '../components/BettingSlipDrawer';
import { buildEventGroups } from '../utils/ticketHelpers';

export function useBetSlipQuote(
  bets: BetItem[],
  betslipMode: 'single' | 'combi' | 'system',
  totalStake: number,
  systemSize: number,
  individualStakes: Record<string, string>
) {
  const [totalOdds, setTotalOdds] = useState<string>('0,00');
  const [possibleWin, setPossibleWin] = useState<string>('0,00');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const validBets = bets.filter((b) => b.eventId && b.marketId && b.outcomeId);
    
    if (validBets.length === 0) {
      setTotalOdds('0,00');
      setPossibleWin('0,00');
      setError(null);
      return;
    }

    const effectiveMode = (betslipMode === 'combi' && validBets.length === 1) ? 'single' : betslipMode;

    const fetchQuote = async () => {
      setLoading(true);
      setError(null);

      try {
        if (effectiveMode === 'single') {
          // For single bets, we quote them individually and sum the potential payouts.
          // Wait, quoteBet for single only accepts one selection.
          // We can use Promise.all to fetch quotes for all single bets.
          let sumPayout = 0;
          const quotePromises = validBets.map(async (bet) => {
            const itemStake = parseFloat(individualStakes[bet.id]) || totalStake || 0;
            if (itemStake <= 0) return 0;
            
            const res = await quoteBet({
              ticket_type: 'single',
              stake: itemStake,
              selections: [{
                event_id: bet.eventId!,
                market_id: bet.marketId!,
                outcome_id: bet.outcomeId!,
                ...(bet.specifiers ? { specifiers: bet.specifiers } : {}),
              }]
            });
            return parseFloat(res.potential_payout) || 0;
          });

          const payouts = await Promise.all(quotePromises);
          sumPayout = payouts.reduce((a, b) => a + b, 0);

          setTotalOdds('0,00'); // Singles don't have total odds
          setPossibleWin(sumPayout.toFixed(2).replace('.', ','));

        } else if (effectiveMode === 'combi') {
          if (totalStake <= 0) {
             setTotalOdds('0,00');
             setPossibleWin('0,00');
             return;
          }

          const event_groups = buildEventGroups(validBets);
          const res = await quoteBet({
            ticket_type: 'acca',
            stake: totalStake,
            event_groups
          });

          setTotalOdds(parseFloat(res.accepted_total_odds).toFixed(2).replace('.', ','));
          setPossibleWin(parseFloat(res.potential_payout).toFixed(2).replace('.', ','));

        } else if (effectiveMode === 'system') {
          const systemSelections = validBets.map(bet => ({
            event_id: bet.eventId!,
            market_id: bet.marketId!,
            outcome_id: bet.outcomeId!,
            specifiers: bet.specifiers,
          }));

          if (totalStake <= 0) {
            setTotalOdds('0,00');
            setPossibleWin('0,00');
            return;
          }

          const res = await quoteBet({
            ticket_type: 'system',
            stake: totalStake,
            selections: systemSelections,
            system: { combination_sizes: [systemSize] }
          });

          setTotalOdds(parseFloat(res.accepted_total_odds).toFixed(2).replace('.', ','));
          setPossibleWin(parseFloat(res.potential_payout).toFixed(2).replace('.', ','));
        }
      } catch (err: any) {
        setError(err.message || 'Error quoting bet');
        // Fallback to 0 if there is an error in quoting
        setTotalOdds('0,00');
        setPossibleWin('0,00');
      } finally {
        setLoading(false);
      }
    };

    const delayDebounceFn = setTimeout(() => {
      fetchQuote();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [bets, betslipMode, totalStake, systemSize, individualStakes]);

  return { totalOdds, possibleWin, loading, error };
}
